import axios from 'axios';
import {
  SubscriptionDto,
  CreateCheckoutSessionDto,
  CheckoutSessionResponse,
  PlanType,
} from '@optidev/shared-types';
import { createAuthHeaders } from '../api-client';

const API_URL = import.meta.env.VITE_API_URL;

export interface PricingResponse {
  currencies: string[];
  defaultCurrency: string;
  plans: {
    standard: { monthly: Record<string, number>; yearly: Record<string, number> };
    pro: { monthly: Record<string, number>; yearly: Record<string, number> };
  };
}

export interface TopUpConfigResponse {
  usdRate: number;
  minCredits: number;
  stepCredits: number;
  pricePerCreditCents: number;
}

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isSubscriptionDefault: boolean;
  isInvoiceDefault: boolean;
}

export interface PaymentMethodsResponse {
  paymentMethods: PaymentMethod[];
  subscriptionDefaultId: string | null;
  invoiceDefaultId: string | null;
}

export const billingApi = {
  /**
   * Get available pricing with currency options
   */
  async getPricing(): Promise<PricingResponse> {
    const response = await axios.get(`${API_URL}/api/billing/pricing`);
    return response.data;
  },

  /**
   * Get current subscription for the workspace
   */
  async getSubscription(token: string): Promise<SubscriptionDto> {
    const response = await axios.get(`${API_URL}/api/billing/subscription`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Create Stripe Checkout Session
   */
  async createCheckoutSession(
    token: string,
    planType: PlanType.STANDARD | PlanType.PRO,
    interval: 'month' | 'year',
    currency?: string,
  ): Promise<CheckoutSessionResponse> {
    const response = await axios.post<CheckoutSessionResponse>(
      `${API_URL}/api/billing/create-checkout-session`,
      { planType, interval, currency },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Upgrade existing subscription (prorated)
   */
  async upgradeSubscription(
    token: string,
    planType: PlanType.STANDARD | PlanType.PRO,
    interval: 'month' | 'year',
  ): Promise<{ success: boolean; message: string }> {
    const response = await axios.post(
      `${API_URL}/api/billing/upgrade-subscription`,
      { planType, interval },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Create Stripe Customer Portal Session
   */
  async createPortalSession(token: string): Promise<CheckoutSessionResponse> {
    const response = await axios.post<CheckoutSessionResponse>(
      `${API_URL}/api/billing/create-portal-session`,
      {},
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Get credit top-up configuration
   */
  async getTopUpConfig(): Promise<TopUpConfigResponse> {
    const response = await axios.get(`${API_URL}/api/billing/top-up-config`);
    return response.data;
  },

  /**
   * Create Stripe Checkout Session for credit top-up
   */
  async topUpCredits(
    token: string,
    credits: number,
  ): Promise<CheckoutSessionResponse> {
    const response = await axios.post<CheckoutSessionResponse>(
      `${API_URL}/api/billing/top-up-credits`,
      { credits },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Check if cloud billing is enabled for the workspace
   */
  async getCloudBillingStatus(token: string): Promise<{ enabled: boolean }> {
    const response = await axios.get<{ enabled: boolean }>(
      `${API_URL}/api/billing/cloud-billing-status`,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Setup cloud billing for free users
   * Returns Stripe Checkout URL to collect payment method
   * @param returnPath - Optional path to return to after setup (e.g., 'publish', 'cloud')
   */
  async setupCloudBilling(
    token: string,
    returnPath?: string,
  ): Promise<CheckoutSessionResponse> {
    const response = await axios.post<CheckoutSessionResponse>(
      `${API_URL}/api/billing/setup-cloud-billing`,
      { returnPath },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Add funds to cloud balance
   * Creates Stripe Checkout session for one-time payment
   * @param amount - Amount in dollars to add
   */
  async addCloudFunds(
    token: string,
    amount: number,
  ): Promise<CheckoutSessionResponse> {
    const response = await axios.post<CheckoutSessionResponse>(
      `${API_URL}/api/billing/cloud-balance/add-funds`,
      { amount },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Get all payment methods for the workspace
   * Returns cards with their roles (subscription default, invoice/auto-replenish default)
   */
  async getPaymentMethods(token: string): Promise<PaymentMethodsResponse> {
    const response = await axios.get<PaymentMethodsResponse>(
      `${API_URL}/api/billing/payment-methods`,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Set invoice default payment method (used for auto-replenish)
   */
  async setInvoiceDefault(
    token: string,
    paymentMethodId: string,
  ): Promise<{ success: boolean }> {
    const response = await axios.post<{ success: boolean }>(
      `${API_URL}/api/billing/set-invoice-default`,
      { paymentMethodId },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Create Stripe Checkout session to add a new card
   * @param setAsInvoiceDefault - Whether to set the new card as invoice default
   */
  async addCard(
    token: string,
    setAsInvoiceDefault: boolean = true,
  ): Promise<CheckoutSessionResponse> {
    const response = await axios.post<CheckoutSessionResponse>(
      `${API_URL}/api/billing/add-card`,
      { setAsInvoiceDefault },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },
};
